import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { listJobs, publishJob, deleteJob } from '@/api/jobs';

import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { StatusTag } from '@/components/ui/status-tag/status-tag';
import { useAuthStore } from '@/stores/authStore';
import type { JobOut } from '@/types';
import { dueCountdown, formatDateTime } from '@/utils/due';

import styles from './jobs.module.css';

/** 作业卡片截止倒计时（学生视角） */
function DueBadge({ job }: { job: JobOut }) {
  const cd = dueCountdown(job.due_date);
  if (cd.level === 'none') return null;
  return (
    <span
      className={`${styles.dueBadge} ${
        cd.level === 'overdue' ? styles.dueOverdue : cd.level === 'urgent' ? styles.dueUrgent : ''
      }`}
    >
      {cd.text}
    </span>
  );
}

/** 学习通式学生作业卡片状态徽标：未交 / 待批阅 / 已批阅+分数 / 已打回 */
function StudentJobBadge({ job }: { job: JobOut }) {
  if (!job.my_submission_status) {
    return <span className={styles.unsubmitted}>未提交</span>;
  }
  if (job.review_status === 'rejected') {
    return <StatusTag status="rejected" />;
  }
  if (
    job.my_submission_status === 'reviewed' ||
    job.review_status === 'confirmed' ||
    job.review_status === 'modified'
  ) {
    return (
      <span className={styles.gradedWrap}>
        <StatusTag status="reviewed" />
        <span className={styles.score}>
          {job.my_score ?? '-'}/{job.max_score}分
        </span>
      </span>
    );
  }
  return <StatusTag status="pending" />;
}

export function JobsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const classIdParam = params.get('classId');
  const classId = classIdParam ? Number(classIdParam) : undefined;
  const isValidClassId = classId !== undefined && !Number.isNaN(classId);

  const user = useAuthStore((state) => state.user);
  const isTeacher = user?.role === 'teacher';

  const queryClient = useQueryClient();
  const [publishingId, setPublishingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [deletingJob, setDeletingJob] = useState<JobOut | null>(null);
  const [deleting, setDeleting] = useState(false);


  const { data, isLoading } = useQuery({
    queryKey: ['jobs', { classId }],
    queryFn: () => listJobs({ class_id: isValidClassId ? classId : undefined }),
  });

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handlePublish = async (e: React.MouseEvent, jobId: number) => {
    e.stopPropagation();
    setPublishingId(jobId);
    try {
      await publishJob(jobId);
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      showToast('发布成功，学生现在可以作答了');
    } catch {
      showToast('发布失败，请重试');
    } finally {
      setPublishingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deletingJob) return;
    setDeleting(true);
    try {
      await deleteJob(deletingJob.id);
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['job', deletingJob.id] });
      showToast('作业已删除');
      setDeletingJob(null);
    } catch {
      showToast('删除失败，请重试');
    } finally {
      setDeleting(false);
    }
  };


  const title = isTeacher ? '作业管理' : '作业列表';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>{title}</h1>
          {isValidClassId && !isTeacher && (
            <button
              type="button"
              className={styles.backLink}
              onClick={() => navigate('/my-classes')}
            >
              ← 返回我的班级
            </button>
          )}
        </div>
        {isTeacher && <Button onClick={() => navigate('/jobs/create')}>新建作业</Button>}
      </header>

      {isLoading && <div className={styles.loading}>加载中...</div>}

      <div className={styles.grid}>
        {data?.items.map((job) => (
          <Card
            key={job.id}
            className={styles.jobCard}
            onClick={() => navigate(`/jobs/${job.id}`)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') navigate(`/jobs/${job.id}`);
            }}
            role="button"
            tabIndex={0}
          >
            <div className={styles.jobHeader}>
              <h2 className={styles.jobTitle}>{job.title}</h2>
              {isTeacher ? <StatusTag status={job.status} /> : <StudentJobBadge job={job} />}
            </div>
            <p className={styles.jobDesc}>{job.description || '暂无描述'}</p>
            <div className={styles.jobMeta}>
              <span>类型：{job.type}</span>
              <span>满分：{job.max_score}</span>
              <span className={styles.timeMeta}>布置：{formatDateTime(job.created_at)}</span>
              <span className={`${styles.timeMeta} ${job.due_date && dueCountdown(job.due_date).level === 'overdue' ? styles.timeOverdue : ''}`}>
                截止：{job.due_date ? formatDateTime(job.due_date) : '未设置'}
              </span>
              {!isTeacher && <DueBadge job={job} />}
            </div>
            {isTeacher && (
              <div className={styles.cardActions}>
                {job.status === 'draft' && (
                  <Button
                    size="small"
                    loading={publishingId === job.id}
                    onClick={(e) => handlePublish(e, job.id)}
                  >
                    发布
                  </Button>
                )}
                <Button
                  size="small"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/jobs/${job.id}/edit`);
                  }}
                >
                  编辑
                </Button>
                <Button
                  size="small"
                  variant="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeletingJob(job);
                  }}
                >
                  删除
                </Button>
              </div>
            )}

          </Card>
        ))}
      </div>

      {data?.items.length === 0 && (
        <div className={styles.empty}>
          {isValidClassId && !isTeacher ? '该班级暂无已发布的作业' : '暂无作业'}
        </div>
      )}

      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}

      {/* 删除作业确认弹窗 */}
      {deletingJob && (
        <div className={styles.deleteMask} onClick={() => !deleting && setDeletingJob(null)}>
          <div className={styles.deleteDialog} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.deleteTitle}>删除作业</h3>
            <p className={styles.deleteDesc}>
              确定要删除作业 <span className={styles.deleteJobName}>{deletingJob.title}</span> 吗？
              <br />
              删除后该作业的所有学生提交、批阅结果、追问记录和错题记录都会一并删除，
              <strong>此操作不可恢复</strong>。
            </p>
            <div className={styles.deleteActions}>
              <Button
                variant="secondary"
                onClick={() => setDeletingJob(null)}
                disabled={deleting}
              >
                取消
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={deleting}>
                确认删除
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

